import React, { useEffect, useRef } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useAuth } from 'react-oidc-context';
import { setAuthTokenGetter } from '../services/api';
import { OIDC_ENABLED } from '../auth/oidcConfig';

/**
 * Auth gate: when Keycloak is configured, requires OIDC login before rendering children.
 * When Keycloak is not configured (local dev), renders children directly.
 */
function AuthGate({ children }) {
  if (!OIDC_ENABLED) {
    return <>{children}</>;
  }
  // OidcGate is a separate component so hooks are called unconditionally within it.
  // It's only mounted when AuthProvider is an ancestor (all 3 OIDC vars set).
  return <OidcGate>{children}</OidcGate>;
}

function OidcGate({ children }) {
  const auth = useAuth();

  // Always-fresh ref to the current auth object. The getter we register with
  // the axios layer reads through this ref, so silent token refreshes from
  // react-oidc-context are picked up on the next API call without us having
  // to re-register on every render. Captured closures can never go stale.
  const authRef = useRef(auth);
  authRef.current = auth;
  // Dedupe concurrent silent refreshes (many polls can expire together)
  const refreshing = useRef(null);
  // A 401 on a token that was just refreshed means the server rejects the
  // session itself; forcing another renew per request would loop on Keycloak
  const lastForced = useRef(0);

  useEffect(() => {
    // Async token provider: refresh when the access token is within 30s of
    // expiry (or when forced after a 401), so requests carry a valid token.
    setAuthTokenGetter(async (forceRefresh) => {
      const a = authRef.current;
      const user = a?.user;
      if (!user) return null;
      const expiringSoon = user.expires_at && (user.expires_at * 1000 - Date.now() < 30000);
      const forceAllowed = forceRefresh && Date.now() - lastForced.current >= 30000;
      if ((forceAllowed || expiringSoon) && typeof a.signinSilent === 'function') {
        if (!refreshing.current) {
          if (forceRefresh) lastForced.current = Date.now();
          refreshing.current = a.signinSilent().finally(() => { refreshing.current = null; });
        }
        try {
          const fresh = await refreshing.current;
          return fresh?.access_token || authRef.current?.user?.access_token || null;
        } catch {
          return authRef.current?.user?.access_token || null;
        }
      }
      return user.access_token;
    });
  }, []);

  // Gate on whether a usable session is held, not on navigator activity: a
  // background token renew (after sleep, or forced by a 401) must not unmount
  // the dashboard and throw away filters and unsaved edits, and a renew error
  // while the token is still valid is retried quietly instead of replacing
  // the app with an error screen.
  const hasUser = Boolean(auth.user);
  const tokenUsable = hasUser && !auth.user.expired;

  if (auth.isLoading && !hasUser) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', gap: 2 }}>
        <CircularProgress size={40} />
        <Typography variant="body2" color="text.secondary">Authenticating...</Typography>
      </Box>
    );
  }

  if (auth.error && !tokenUsable) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', gap: 2 }}>
        <Typography color="error">Authentication error: {auth.error.message}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ cursor: 'pointer', textDecoration: 'underline' }}
          onClick={() => auth.signinRedirect()}>
          Try again
        </Typography>
      </Box>
    );
  }

  return <AuthedOrRedirect auth={auth}>{children}</AuthedOrRedirect>;
}

function AuthedOrRedirect({ auth, children }) {
  // Kick off the redirect in an effect, guarded so it fires exactly once and
  // never mid-render. Calling signinRedirect() in the render body re-fires on
  // every re-render (and doubles under StrictMode), racing its own OIDC state
  // nonce and producing intermittent "no matching state" login failures.
  const redirected = useRef(false);
  useEffect(() => {
    if (!auth.isAuthenticated && !auth.activeNavigator && !redirected.current) {
      redirected.current = true;
      // A failed redirect (IdP unreachable, storage blocked) must not strand
      // the user on the spinner: allow the next state change to retry
      auth.signinRedirect().catch(() => { redirected.current = false; });
    }
  }, [auth.isAuthenticated, auth.activeNavigator, auth]);

  if (!auth.isAuthenticated) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', gap: 2 }}>
        <CircularProgress size={40} />
        <Typography variant="body2" color="text.secondary">Redirecting to login...</Typography>
      </Box>
    );
  }

  return <>{children}</>;
}

export default AuthGate;
